import { useState, useCallback, useMemo, useRef } from "react";
import clsx from "clsx";
import { AddIcon as PlusIcon, CloseIcon as XIcon } from "../../atoms/Icon";

import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    type MonthlyShiftCellDisplaySettings,
    type EmployeeCellAreaOption,
    type ShiftCellAreaOption,
    type EmployeeRowConfig,
    type ShiftCellRowConfig,
    type ShiftPreferenceConfig,
    type ShiftPreferenceContentOption,
    type SampleEmployeeData,
    type SampleShiftData,
    type SampleShiftPreferenceData,
    EMPLOYEE_CELL_OPTIONS,
    EMPLOYEE_ROW1_OPTIONS,
    SECONDARY_SHIFT_OPTIONS,
    SHIFT_PREFERENCE_OPTIONS,
    MAX_ROWS,
    getCellHeight,
    getEmployeeDisplayText,
    getShiftDisplayText,
    getShiftPreferenceDisplayText,
    SAMPLE_SHIFT_DATA,
    EXTRA_ROW_HEIGHT,
} from "../../../shared/monthlyShiftSettings";

export interface MonthlyShiftCellSettingsProps {
    /** 現在の設定値 */
    value: MonthlyShiftCellDisplaySettings;
    /** 設定変更時のコールバック */
    onChange: (settings: MonthlyShiftCellDisplaySettings) => void;
    /** 読み取り専用 */
    readOnly?: boolean;
}

// ============================================================================
// サンプルデータ（プレビュー用）
// ============================================================================

interface PreviewShift extends SampleShiftData {
    /** プレビュー用：希望表示テキスト（べた書き） */
    preferenceText?: string;
}

type PreviewHolidayShift = {
    isHoliday: true;
    holidayName?: string;
    /** プレビュー用：希望表示テキスト（べた書き） */
    preferenceText?: string;
    backgroundColor?: string;
    textColor?: string;
};

const SAMPLE_EMPLOYEE: SampleEmployeeData & {
    shifts: (PreviewShift | PreviewHolidayShift)[];
} = {
    employeeName: "サンプル従業員",
    employmentType: "正社員",
    employmentTypeCode: "101",
    employeeCode: "EMP001",
    monthlyContractHours: "160h / 200h",
    monthlyHolidayCount: "8日 / 10日",
    monthlyWorkSummary: "21日 / 9日 / 176h",
    shifts: [
        { ...SAMPLE_SHIFT_DATA, patternCode: "", startTime: "10:00", endTime: "18:30", breakMinutes: 60, breakTime: "13:00-14:00", supportStoreName: "", comment: "", workHours: "7.5h", isHoliday: false, holidayName: "", preferenceText: "8:00-20:00" },
        { ...SAMPLE_SHIFT_DATA, patternCode: "鍵開け早番", startTime: "08:00", endTime: "16:30", breakMinutes: 60, breakTime: "12:00-13:00", supportStoreName: "", comment: "", workHours: "7.5h", isHoliday: false, holidayName: "", preferenceText: "" },
        { ...SAMPLE_SHIFT_DATA, patternCode: "中番ショート", startTime: "11:00", endTime: "17:00", breakMinutes: 30, breakTime: "13:00-13:30", supportStoreName: "", comment: "", workHours: "5.5h", isHoliday: false, holidayName: "", preferenceText: "8:00-17:00" },
        { isHoliday: true as const, holidayName: "公休", backgroundColor: "#b8f3d2", textColor: "#374151", preferenceText: "" },
        { isHoliday: true as const, holidayName: "有休", backgroundColor: "#ffd3cb", textColor: "#1f2937", preferenceText: "有休" },
    ],
};

const DATE_HEADERS = [
    { month: 1, day: 1, dow: "日", isSunday: true, isSaturday: false },
    { month: 1, day: 2, dow: "月", isSunday: false, isSaturday: false },
    { month: 1, day: 3, dow: "火", isSunday: false, isSaturday: false },
    { month: 1, day: 4, dow: "水", isSunday: false, isSaturday: false },
    { month: 1, day: 5, dow: "木", isSunday: false, isSaturday: false },
];

// ============================================================================
// ヘルパー関数
// ============================================================================

/** シフトサンプルデータの表示テキスト取得（休日対応） */
const getShiftPreviewText = (
    option: ShiftCellAreaOption,
    shift: SampleShiftData | { isHoliday: true; holidayName?: string }
): string => {
    if ("isHoliday" in shift && shift.isHoliday === true && !("patternCode" in shift)) {
        if (option === "shiftInfo") return shift.holidayName ?? "";
        return "";
    }
    return getShiftDisplayText(option, shift as SampleShiftData);
};

// ============================================================================
// メインコンポーネント
// ============================================================================

/**
 * 月間シフト表示設定コンポーネント（v2: リストベースUI）
 */
export const MonthlyShiftCellSettings = ({
    value,
    onChange,
    readOnly = false,
}: MonthlyShiftCellSettingsProps) => {
    const [previewMode, setPreviewMode] = useState<"normal" | "preference">("normal");
    const [activeEmployeeId, setActiveEmployeeId] = useState<string | null>(null);
    const [activeShiftId, setActiveShiftId] = useState<string | null>(null);

    // dnd-kit センサー
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // 安定ID管理（並列配列でアイテムとIDが1:1対応）
    const empIdCounter = useRef(0);
    const empIdsRef = useRef<string[]>([]);
    const shiftIdCounter = useRef(0);
    const shiftIdsRef = useRef<string[]>([]);

    // 従業員行の安定ID（1行目は固定なので除外）
    const employeeRowsWithIds = useMemo(() => {
        const sortableRows = value.employeeRows.slice(1);
        const prev = empIdsRef.current;
        // 行数が増えた分だけ新規IDを末尾に追加
        const ids = [...prev];
        while (ids.length < sortableRows.length) {
            ids.push(`emp-${empIdCounter.current++}`);
        }
        // 行数が減った分は末尾を切り詰め
        ids.length = sortableRows.length;
        empIdsRef.current = ids;
        return sortableRows.map((row, i) => ({
            row,
            arrayIdx: i + 1,
            id: ids[i],
        }));
    }, [value.employeeRows]);

    const employeeSortIds = useMemo(
        () => employeeRowsWithIds.map((r) => r.id),
        [employeeRowsWithIds],
    );

    // シフト行の安定ID（1行目は固定なので除外）
    const shiftRowsWithIds = useMemo(() => {
        const sortableRows = value.shiftRows.slice(1);
        const prev = shiftIdsRef.current;
        const ids = [...prev];
        while (ids.length < sortableRows.length) {
            ids.push(`shift-${shiftIdCounter.current++}`);
        }
        ids.length = sortableRows.length;
        shiftIdsRef.current = ids;
        return sortableRows.map((row, i) => ({
            row,
            arrayIdx: i + 1,
            id: ids[i],
        }));
    }, [value.shiftRows]);

    const shiftSortIds = useMemo(
        () => shiftRowsWithIds.map((r) => r.id),
        [shiftRowsWithIds],
    );

    const preference: ShiftPreferenceConfig = value.shiftPreference ?? {
        enabled: false,
        content: "patternCode",
        position: "bottom",
    };

    const baseCellHeight = getCellHeight(value.employeeRows.length, value.shiftRows.length, false);
    const cellHeight = previewMode === "preference" ? baseCellHeight + EXTRA_ROW_HEIGHT : baseCellHeight;

    // --- 従業員セル操作 ---
    const updateEmployeeRow = useCallback((rowIndex: number, content: EmployeeCellAreaOption) => {
        const newRows = [...value.employeeRows];
        newRows[rowIndex] = { content };
        onChange({ ...value, employeeRows: newRows });
    }, [value, onChange]);

    const addEmployeeRow = useCallback(() => {
        if (value.employeeRows.length >= MAX_ROWS) return;
        onChange({ ...value, employeeRows: [...value.employeeRows, { content: "none" }] });
    }, [value, onChange]);

    const removeEmployeeRow = useCallback((rowIndex: number) => {
        if (rowIndex === 0) return; // 1行目は削除不可
        // ID配列からも削除（sortable対象はslice(1)なのでindex-1）
        const ids = [...empIdsRef.current];
        ids.splice(rowIndex - 1, 1);
        empIdsRef.current = ids;
        onChange({ ...value, employeeRows: value.employeeRows.filter((_, i) => i !== rowIndex) });
    }, [value, onChange]);

    const reorderEmployeeRows = useCallback((fromIndex: number, toIndex: number) => {
        if (fromIndex <= 0 || toIndex <= 0) return; // 1行目は移動不可
        // ID配列も同じ順序で並べ替え（sortable対象はslice(1)なのでindex-1）
        const ids = [...empIdsRef.current];
        const [movedId] = ids.splice(fromIndex - 1, 1);
        ids.splice(toIndex - 1, 0, movedId);
        empIdsRef.current = ids;
        const newRows = [...value.employeeRows];
        const [moved] = newRows.splice(fromIndex, 1);
        newRows.splice(toIndex, 0, moved);
        onChange({ ...value, employeeRows: newRows });
    }, [value, onChange]);

    // --- シフトセル操作 ---
    const updateShiftRow = useCallback((rowIndex: number, content: ShiftCellAreaOption) => {
        const newRows = [...value.shiftRows];
        newRows[rowIndex] = { content };
        onChange({ ...value, shiftRows: newRows });
    }, [value, onChange]);

    const addShiftRow = useCallback(() => {
        if (value.shiftRows.length >= MAX_ROWS) return;
        onChange({ ...value, shiftRows: [...value.shiftRows, { content: "none" }] });
    }, [value, onChange]);

    const removeShiftRow = useCallback((rowIndex: number) => {
        if (rowIndex === 0) return;
        const ids = [...shiftIdsRef.current];
        ids.splice(rowIndex - 1, 1);
        shiftIdsRef.current = ids;
        onChange({ ...value, shiftRows: value.shiftRows.filter((_, i) => i !== rowIndex) });
    }, [value, onChange]);

    const reorderShiftRows = useCallback((fromIndex: number, toIndex: number) => {
        if (fromIndex <= 0 || toIndex <= 0) return;
        const ids = [...shiftIdsRef.current];
        const [movedId] = ids.splice(fromIndex - 1, 1);
        ids.splice(toIndex - 1, 0, movedId);
        shiftIdsRef.current = ids;
        const newRows = [...value.shiftRows];
        const [moved] = newRows.splice(fromIndex, 1);
        newRows.splice(toIndex, 0, moved);
        onChange({ ...value, shiftRows: newRows });
    }, [value, onChange]);

    // --- シフト希望操作 ---
    const updatePreference = useCallback((updates: Partial<ShiftPreferenceConfig>) => {
        onChange({ ...value, shiftPreference: { ...preference, ...updates } });
    }, [value, preference, onChange]);


    // --- ドラッグ＆ドロップ (dnd-kit) ---
    // IDから配列インデックスを解決
    const findEmployeeArrayIdx = useCallback((id: string) => {
        return employeeRowsWithIds.find((r) => r.id === id)?.arrayIdx ?? -1;
    }, [employeeRowsWithIds]);
    const findShiftArrayIdx = useCallback((id: string) => {
        return shiftRowsWithIds.find((r) => r.id === id)?.arrayIdx ?? -1;
    }, [shiftRowsWithIds]);

    const handleEmployeeDragStart = useCallback((event: DragStartEvent) => {
        setActiveEmployeeId(String(event.active.id));
    }, []);
    const handleEmployeeDragEnd = useCallback((event: DragEndEvent) => {
        setActiveEmployeeId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const fromIndex = findEmployeeArrayIdx(String(active.id));
        const toIndex = findEmployeeArrayIdx(String(over.id));
        if (fromIndex >= 0 && toIndex >= 0) {
            reorderEmployeeRows(fromIndex, toIndex);
        }
    }, [reorderEmployeeRows, findEmployeeArrayIdx]);

    const handleShiftDragStart = useCallback((event: DragStartEvent) => {
        setActiveShiftId(String(event.active.id));
    }, []);
    const handleShiftDragEnd = useCallback((event: DragEndEvent) => {
        setActiveShiftId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const fromIndex = findShiftArrayIdx(String(active.id));
        const toIndex = findShiftArrayIdx(String(over.id));
        if (fromIndex >= 0 && toIndex >= 0) {
            reorderShiftRows(fromIndex, toIndex);
        }
    }, [reorderShiftRows, findShiftArrayIdx]);

    // ドラッグ中のオーバーレイ用データ取得
    const activeEmployeeRow = useMemo(() => {
        if (!activeEmployeeId) return null;
        const found = employeeRowsWithIds.find((r) => r.id === activeEmployeeId);
        if (!found) return null;
        return { idx: found.arrayIdx, row: found.row };
    }, [activeEmployeeId, employeeRowsWithIds]);

    const activeShiftRow = useMemo(() => {
        if (!activeShiftId) return null;
        const found = shiftRowsWithIds.find((r) => r.id === activeShiftId);
        if (!found) return null;
        return { idx: found.arrayIdx, row: found.row };
    }, [activeShiftId, shiftRowsWithIds]);

    // ============================================================================
    // 従業員セル設定パネル（カード型UI + dnd-kit）
    // ============================================================================
    const renderEmployeeCellConfig = () => (
        <div className="w-80 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">従業員セル</h3>
            <div className="space-y-2">
                {/* 1行目（固定・ドラッグ不可） */}
                <EmployeeCardFixed />

                {/* 2行目以降（ソータブル） */}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleEmployeeDragStart} onDragEnd={handleEmployeeDragEnd}>
                    <SortableContext items={employeeSortIds} strategy={verticalListSortingStrategy}>
                        {employeeRowsWithIds.map(({ row, arrayIdx, id }) => (
                            <EmployeeCardSortable
                                key={id}
                                id={id}
                                idx={arrayIdx}
                                row={row}
                                readOnly={readOnly}
                                onUpdate={updateEmployeeRow}
                                onRemove={removeEmployeeRow}
                            />
                        ))}
                    </SortableContext>
                    <DragOverlay dropAnimation={null}>
                        {activeEmployeeRow && (
                            <EmployeeCardOverlay idx={activeEmployeeRow.idx} row={activeEmployeeRow.row} />
                        )}
                    </DragOverlay>
                </DndContext>

                {/* 段を追加ボタン（ダッシュボーダーカード） */}
                {value.employeeRows.length < MAX_ROWS && !readOnly && (
                    <button
                        type="button"
                        onClick={addEmployeeRow}
                        className="w-full py-3 rounded-lg border-2 border-dashed border-brand-200 text-brand-500 text-sm font-medium hover:bg-brand-50 hover:border-brand-400 transition-all flex items-center justify-center gap-1"
                    >
                        <PlusIcon className="w-4 h-4" />
                        段を追加
                    </button>
                )}
            </div>
        </div>
    );

    // ============================================================================
    // シフトセル設定パネル
    // ============================================================================
    const renderShiftCellConfig = () => (
        <div className="w-80 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">シフトセル</h3>
            <div className="space-y-2">
                {/* 1段目（固定） */}
                <ShiftCardFixed />

                {/* 2段目以降（ソータブル） */}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleShiftDragStart} onDragEnd={handleShiftDragEnd}>
                    <SortableContext items={shiftSortIds} strategy={verticalListSortingStrategy}>
                        {shiftRowsWithIds.map(({ row, arrayIdx, id }) => (
                            <ShiftCardSortable
                                key={id}
                                id={id}
                                idx={arrayIdx}
                                row={row}
                                readOnly={readOnly}
                                onUpdate={updateShiftRow}
                                onRemove={removeShiftRow}
                            />
                        ))}
                    </SortableContext>
                    <DragOverlay dropAnimation={null}>
                        {activeShiftRow && (
                            <ShiftCardOverlay idx={activeShiftRow.idx} row={activeShiftRow.row} />
                        )}
                    </DragOverlay>
                </DndContext>

                {/* 段を追加 */}
                {value.shiftRows.length < MAX_ROWS && !readOnly && (
                    <button
                        type="button"
                        onClick={addShiftRow}
                        className="w-full py-3 rounded-lg border-2 border-dashed border-brand-200 text-brand-500 text-sm font-medium hover:bg-brand-50 hover:border-brand-400 transition-all flex items-center justify-center gap-1"
                    >
                        <PlusIcon className="w-4 h-4" />
                        段を追加
                    </button>
                )}

                {/* シフト希望（最下段固定） */}
                <ShiftPreferenceCardFixed />
            </div>
        </div>
    );

    // ============================================================================
    // プレビュー
    // ============================================================================
    const renderPreview = () => {
        const showPreference = previewMode === "preference";

        return (
            <div className="min-w-0">
                {/* ヘッダー: タイトル + モード切替 */}
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-800">プレビュー</h3>
                    <div className="flex bg-gray-100 rounded-md p-0.5">
                        <button
                            type="button"
                            onClick={() => setPreviewMode("normal")}
                            className={clsx(
                                "px-3 py-1 text-xs rounded transition-colors",
                                previewMode === "normal"
                                    ? "bg-white text-gray-800 shadow-sm font-medium"
                                    : "text-gray-500 hover:text-gray-700"
                            )}
                        >
                            通常
                        </button>
                        <button
                            type="button"
                            onClick={() => setPreviewMode("preference")}
                            className={clsx(
                                "px-3 py-1 text-xs rounded transition-colors",
                                previewMode === "preference"
                                    ? "bg-white text-gray-800 shadow-sm font-medium"
                                    : "text-gray-500 hover:text-gray-700"
                            )}
                        >
                            希望表示
                        </button>
                    </div>
                </div>

                {/* グリッド（実際のShiftGridと同じサイズ・見た目） */}
                <div className="border border-gray-300 overflow-x-auto bg-white">
                    <table className="border-separate border-spacing-0 table-fixed">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="sticky left-0 z-10 bg-gray-50 border-r border-b border-gray-200 w-44 min-w-44" />
                                {DATE_HEADERS.map((date, idx) => {
                                    const dayColor = date.isSunday ? "text-red-500" : date.isSaturday ? "text-blue-500" : "text-gray-900";
                                    return (
                                        <th
                                            key={idx}
                                            className={clsx(
                                                "border-b border-r border-gray-200 px-1 py-1 text-center font-normal w-20 min-w-20",
                                                date.isSunday ? "border-r-gray-400" : "border-r-gray-200"
                                            )}
                                        >
                                            <div className="text-xs text-gray-900">{date.month}/{date.day}</div>
                                            <div className="text-xs text-gray-900">
                                                (<span className={dayColor}>{date.dow}</span>)
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="hover:bg-gray-50">
                                {/* 従業員セル */}
                                <td
                                    className="sticky left-0 z-10 bg-white border-r border-b border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                                    style={{ height: cellHeight }}
                                >
                                    <div className="px-3 py-2 h-full flex flex-col justify-center text-left">
                                        {value.employeeRows.map((row, rowIdx) => {
                                            const text = getEmployeeDisplayText(row.content, SAMPLE_EMPLOYEE);
                                            const isFirstRow = rowIdx === 0;

                                            return (
                                                <div
                                                    key={rowIdx}
                                                    className={clsx(
                                                        "flex items-center justify-between",
                                                        rowIdx > 0 && "mt-0.5"
                                                    )}
                                                >
                                                    <span className={clsx(
                                                        "truncate",
                                                        isFirstRow ? "text-sm font-medium text-gray-900" : "text-xs text-gray-500"
                                                    )}>
                                                        {text}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </td>

                                {/* シフトセル */}
                                {SAMPLE_EMPLOYEE.shifts.map((shift, dayIdx) => {
                                    const isHolidayCell = "isHoliday" in shift && shift.isHoliday === true && !("patternCode" in shift);
                                    const hasBgColor = isHolidayCell && !!(shift as PreviewHolidayShift).backgroundColor;
                                    const isSundayCell = DATE_HEADERS[dayIdx]?.isSunday;

                                    return (
                                        <td
                                            key={dayIdx}
                                            className={clsx(
                                                "border-b border-r bg-white p-0.5 w-20 min-w-20 relative",
                                                isSundayCell ? "border-r-gray-400" : "border-r-gray-200",
                                                "border-b-gray-200"
                                            )}
                                            style={{ height: cellHeight }}
                                        >
                                            {/* 希望提出ありインジケーター（通常モード時） */}
                                            {!!shift.preferenceText && (
                                                <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
                                            )}
                                            <div className="w-full h-full flex flex-col">
                                                {/* シフト情報エリア（背景色はここだけ） */}
                                                <div
                                                    className={clsx("flex items-center justify-center", showPreference ? "flex-shrink-0" : "flex-1")}
                                                    style={{
                                                        ...(showPreference ? { height: baseCellHeight - 4 } : {}),
                                                        ...(hasBgColor ? { backgroundColor: (shift as PreviewHolidayShift).backgroundColor } : {}),
                                                    }}
                                                >
                                                    <div className="flex flex-col items-center justify-center w-full overflow-hidden"
                                                        style={hasBgColor ? { color: (shift as PreviewHolidayShift).textColor } : undefined}
                                                    >
                                                        {value.shiftRows.map((sRow, rowIdx) => {
                                                            const text = getShiftPreviewText(sRow.content, shift);
                                                            if (!text && rowIdx > 0) return null;
                                                            return (
                                                                <div key={rowIdx} className={clsx(
                                                                    "text-[11px] leading-tight truncate max-w-full px-0.5",
                                                                    !hasBgColor && "text-gray-900"
                                                                )}>
                                                                    {rowIdx === 0
                                                                        ? <span className="font-medium">{text || (isHolidayCell ? "休" : "")}</span>
                                                                        : text}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* 希望表示モード: グレー区切り線 + シフト希望情報 */}
                                                {showPreference && (
                                                    <div className="flex flex-col items-center py-1">
                                                        <div className="w-4/5 border-t border-gray-300" />
                                                        <div className="text-[11px] leading-tight truncate max-w-full px-0.5 pt-1 text-gray-900">
                                                            {shift.preferenceText || "-"}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ============================================================================
    // レイアウト
    // ============================================================================
    return (
        <div>
            <div className="text-sm text-gray-600 mb-4">
                シフト表のセルに表示する内容を設定します。
            </div>
            <div className="flex gap-6 items-start">
                {renderEmployeeCellConfig()}
                {renderShiftCellConfig()}
                {renderPreview()}
            </div>
        </div>
    );
};

// ============================================================================
// 従業員カードコンポーネント（1行目固定）
// ============================================================================

const EmployeeCardFixed = () => (
    <div className="rounded-lg border border-gray-200 bg-white py-3 flex">
        <div className="flex items-center justify-center pl-2 pr-1 flex-shrink-0">
            <span className="text-xl select-none leading-none invisible">⠿</span>
        </div>
        <div className="flex-1 min-w-0 pr-4 pl-2">
            <div className="flex items-center gap-2">
                <span className="text-sm font-semibold flex-shrink-0 text-brand-600">1段目</span>
                <span className="text-sm font-semibold text-gray-800 flex-1">従業員名</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-0.5 rounded-full font-medium flex-shrink-0">固定</span>
            </div>
        </div>
    </div>
);

// ============================================================================
// 従業員カードコンポーネント（2行目以降ソータブル）
// ============================================================================

interface EmployeeCardSortableProps {
    id: string;
    idx: number;
    row: EmployeeRowConfig;
    readOnly: boolean;
    onUpdate: (idx: number, content: EmployeeCellAreaOption) => void;
    onRemove: (idx: number) => void;
}

const EmployeeCardSortable = ({ id, idx, row, readOnly, onUpdate, onRemove }: EmployeeCardSortableProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={clsx(
                "rounded-lg border py-3 flex transition-shadow",
                isDragging ? "border-dashed border-gray-300 bg-gray-50 opacity-50" : "border-gray-200 bg-white",
            )}
        >
            {/* ドラッグハンドル */}
            <div
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                className="flex items-center justify-center pl-2 pr-1 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
            >
                <span className="text-gray-300 hover:text-gray-400 text-xl select-none leading-none">⠿</span>
            </div>
            {/* カード本体 */}
            <div className="flex-1 min-w-0 pr-4 pl-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold flex-shrink-0 text-brand-600">{idx + 1}段目</span>
                    <span className="flex-1" />
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => onRemove(idx)}
                            className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
                            title="この段を削除"
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <div className="mt-2">
                    <select
                        value={row.content}
                        onChange={(e) => onUpdate(idx, e.target.value as EmployeeCellAreaOption)}
                        disabled={readOnly}
                        className={clsx(
                            "w-full text-sm bg-white border border-gray-200 rounded-md px-3 py-1.5 outline-none cursor-pointer focus:border-brand-400 focus:ring-1 focus:ring-brand-400",
                            row.content === "none" ? "text-gray-400" : "text-gray-700"
                        )}
                    >
                        <option value="none" disabled className="text-gray-400">選択してください</option>
                        {EMPLOYEE_CELL_OPTIONS.filter((opt) => opt.value !== "none").map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// シフトカードコンポーネント（1段目固定）
// ============================================================================

const ShiftCardFixed = () => (
    <div className="rounded-lg border border-gray-200 bg-white py-3 flex">
        <div className="flex items-center justify-center pl-2 pr-1 flex-shrink-0">
            <span className="text-xl select-none leading-none invisible">⠿</span>
        </div>
        <div className="flex-1 min-w-0 pr-4 pl-2">
            <div className="flex items-center gap-2">
                <span className="text-sm font-semibold flex-shrink-0 text-brand-600">1段目</span>
                <span className="text-sm font-semibold text-gray-800 flex-1">シフト情報</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-0.5 rounded-full font-medium flex-shrink-0">固定</span>
            </div>
        </div>
    </div>
);

// ============================================================================
// シフトカードコンポーネント（2段目以降ソータブル）
// ============================================================================

interface ShiftCardSortableProps {
    id: string;
    idx: number;
    row: ShiftCellRowConfig;
    readOnly: boolean;
    onUpdate: (idx: number, content: ShiftCellAreaOption) => void;
    onRemove: (idx: number) => void;
}

const ShiftCardSortable = ({ id, idx, row, readOnly, onUpdate, onRemove }: ShiftCardSortableProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={clsx(
                "rounded-lg border py-3 flex transition-shadow",
                isDragging ? "border-dashed border-gray-300 bg-gray-50 opacity-50" : "border-gray-200 bg-white",
            )}
        >
            {/* ドラッグハンドル */}
            <div
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                className="flex items-center justify-center pl-2 pr-1 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
            >
                <span className="text-gray-300 hover:text-gray-400 text-xl select-none leading-none">⠿</span>
            </div>
            {/* カード本体 */}
            <div className="flex-1 min-w-0 pr-4 pl-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold flex-shrink-0 text-brand-600">{idx + 1}段目</span>
                    <span className="flex-1" />
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => onRemove(idx)}
                            className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
                            title="この段を削除"
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <div className="mt-2">
                    <select
                        value={row.content}
                        onChange={(e) => onUpdate(idx, e.target.value as ShiftCellAreaOption)}
                        disabled={readOnly}
                        className={clsx(
                            "w-full text-sm bg-white border border-gray-200 rounded-md px-3 py-1.5 outline-none cursor-pointer focus:border-brand-400 focus:ring-1 focus:ring-brand-400",
                            row.content === "none" ? "text-gray-400" : "text-gray-700"
                        )}
                    >
                        <option value="none" disabled>選択してください</option>
                        {SECONDARY_SHIFT_OPTIONS.filter((opt) => opt.value !== "none").map((opt) => (
                            <option key={opt.value} value={opt.value} className="text-gray-700">{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// シフト希望カード（最下段固定）
// ============================================================================

const ShiftPreferenceCardFixed = () => (
    <div className="rounded-lg border border-gray-200 bg-white py-3 flex">
        <div className="flex items-center justify-center pl-2 pr-1 flex-shrink-0">
            <span className="text-xl select-none leading-none invisible">⠿</span>
        </div>
        <div className="flex-1 min-w-0 pr-4 pl-2">
            <div className="flex items-center gap-2">
                <span className="text-sm font-semibold flex-shrink-0 text-brand-600">最下段</span>
                <span className="text-sm font-semibold text-gray-800 flex-1">シフト希望情報</span>
                <span className="text-xs text-amber-600 bg-amber-100 px-2.5 py-0.5 rounded-full font-medium flex-shrink-0">希望表示モード</span>
            </div>
        </div>
    </div>
);

// ============================================================================
// DragOverlay 用カード（ドラッグ中に浮いて表示される）
// ============================================================================

const EmployeeCardOverlay = ({ idx, row }: { idx: number; row: EmployeeRowConfig }) => {
    const label = EMPLOYEE_CELL_OPTIONS.find((o) => o.value === row.content)?.label ?? "選択してください";
    return (
        <div className="rounded-lg border border-brand-400 bg-white py-3 flex shadow-xl ring-2 ring-brand-200 w-80">
            <div className="flex items-center justify-center pl-2 pr-1 flex-shrink-0">
                <span className="text-brand-400 text-xl select-none leading-none">⠿</span>
            </div>
            <div className="flex-1 min-w-0 pr-4 pl-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold flex-shrink-0 text-brand-600">{idx + 1}段目</span>
                </div>
                <div className="mt-2">
                    <div className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 text-gray-700 bg-gray-50">
                        {label}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ShiftCardOverlay = ({ idx, row }: { idx: number; row: ShiftCellRowConfig }) => {
    const label = SECONDARY_SHIFT_OPTIONS.find((o) => o.value === row.content)?.label ?? "選択してください";
    return (
        <div className="rounded-lg border border-brand-400 bg-white py-3 flex shadow-xl ring-2 ring-brand-200 w-80">
            <div className="flex items-center justify-center pl-2 pr-1 flex-shrink-0">
                <span className="text-brand-400 text-xl select-none leading-none">⠿</span>
            </div>
            <div className="flex-1 min-w-0 pr-4 pl-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold flex-shrink-0 text-brand-600">{idx + 1}段目</span>
                </div>
                <div className="mt-2">
                    <div className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 text-gray-700 bg-gray-50">
                        {label}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// アイコンコンポーネント
// ============================================================================

const ChevronRightIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
);




export default MonthlyShiftCellSettings;
